import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import AdminTable from "components/admin/AdminTable";
import GameIcon from "components/admin/GameIcon";
import { useFetch } from "components/admin/hooks";
import api from "api/client";
import { formatNumber } from "utils/format";
import { rangeText } from "layouts/templates/labels";
import { Choice, NumField } from "layouts/simulator/fields";

/** Lấy cả bảng một lần cho mỗi loại; MAX_PAGE_SIZE của máy chủ là 200. */
const PAGE_SIZE = 200;

const SORTS = [
  { value: "id", label: "Theo ID" },
  { value: "atk", label: "Công cao nhất" },
  { value: "def", label: "Thủ cao nhất" },
  { value: "hp", label: "HP cao nhất" },
  { value: "mp", label: "MP cao nhất" },
  { value: "require", label: "Dễ đeo nhất" },
];

const topOf = (range) => (range && range.length ? range[range.length - 1] : 0);

/** Số điểm còn thiếu ở từng chỉ số để đeo được món này. */
function missingFor(item, petStats) {
  if (!petStats) return null;
  const gap = [
    ["sức", item.requireStr - petStats.str],
    ["nhanh", item.requireAgi - petStats.agi],
    ["trí", item.requireInt - petStats.int],
  ].filter(([, need]) => need > 0);
  return { ok: gap.length === 0, gap };
}

const BEST_STATS = [
  { value: "atk", label: "Công cao nhất" },
  { value: "def", label: "Thủ cao nhất" },
  { value: "hp", label: "HP cao nhất" },
  { value: "mp", label: "MP cao nhất" },
  { value: "power", label: "Mạnh nhất tổng thể" },
];

/** Bảng "bộ tốt nhất": mỗi ô một món, kèm nút mặc cả bộ. */
function BestSet({ stat, onStat, rows, loading, error, onPickAll, onPick, meta }) {
  const [lvl, setLvl] = useState(meta.maxEnchant);
  const [fusion, setFusion] = useState(meta.maxFusion);
  const usable = rows.filter((r) => !r.empty);
  const noFusion = usable.filter((r) => !r.canFusion);

  /** Món không dung hợp được thì ép về ★0, đúng như máy chủ sẽ làm. */
  const withUpgrades = () =>
    usable.map((r) => ({
      type: r.type,
      templateId: r.templateId,
      lvl: Math.min(lvl, r.type === meta.wingType ? meta.maxEnchantWing : meta.maxEnchant),
      fusion: r.canFusion ? fusion : 0,
    }));

  return (
    <MDBox>
      {/* flex-start: hai ô "Cường hoá" / "Dung hợp" có chú thích bên dưới nên cao hơn ô chọn */}
      <Grid container spacing={2} alignItems="flex-start" mb={1}>
        <Grid item xs={12} md={3}>
          <Choice label="Chọn bộ theo" value={stat} options={BEST_STATS} onChange={onStat} />
        </Grid>
        <Grid item xs={6} md={2}>
          <NumField
            label="Cường hoá"
            value={lvl}
            min={0}
            max={meta.maxEnchant}
            onChange={setLvl}
            helper={`tối đa +${meta.maxEnchant}`}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <NumField
            label="Dung hợp"
            value={fusion}
            min={0}
            max={meta.maxFusion}
            onChange={setFusion}
            helper={`tối đa ★${meta.maxFusion}`}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <MDButton
            variant="gradient"
            color="success"
            disabled={usable.length === 0}
            onClick={() => onPickAll(withUpgrades())}
          >
            <Icon>done_all</Icon>&nbsp;
            {`Mặc cả bộ (${usable.length} món) +${lvl}★${fusion}`}
          </MDButton>
        </Grid>
      </Grid>
      {fusion > 0 && noFusion.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {`${noFusion.length} món sẽ được mặc ở ★0 vì không dung hợp được: ${noFusion
            .map((r) => r.label)
            .join(", ")}. Trong game chỉ trang bị lev cuối của chuỗi tiến hoá mới dung hợp được.`}
        </Alert>
      )}
      {fusion > 0 && lvl < meta.fusionEnchantBaseLvl && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {`Trong game phải cường hoá tới +${meta.fusionEnchantBaseLvl} mới dung hợp được, nên bộ ở +${lvl} mà có sao là trạng thái không đạt tới được.`}
        </Alert>
      )}
      <MDTypography variant="caption" color="text" display="block" mb={1}>
        Xếp hạng theo chỉ số gốc trần của mẫu. Cường hoá và dung hợp nhân lên chỉ số gốc theo cùng
        một tỉ lệ cho mọi món, nên thứ tự ở +0 cũng là thứ tự ở +10★20.
      </MDTypography>
      {error && <Alert severity="error">{error}</Alert>}
      <AdminTable
        loading={loading}
        rows={rows}
        rowKey="type"
        emptyText="Không tìm được món nào"
        columns={[
          { key: "label", label: "Ô", render: (r) => r.label },
          {
            key: "name",
            label: "Món tốt nhất",
            wrap: true,
            render: (r) =>
              r.empty ? (
                <MDTypography variant="caption" color="text">
                  không có món nào hợp lệ
                </MDTypography>
              ) : (
                <MDBox display="flex" alignItems="center" gap={1.5}>
                  <GameIcon path={r.icon} size={28} fallback="shield" />
                  <MDBox minWidth={0}>
                    <MDTypography variant="button" fontWeight="medium" display="block">
                      {r.name}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {`#${r.templateId}`}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              ),
          },
          {
            key: "stats",
            label: "Chỉ số gốc",
            wrap: true,
            render: (r) =>
              r.empty
                ? "-"
                : [
                    ["công", r.baseStats.atk],
                    ["thủ", r.baseStats.def],
                    ["HP", r.baseStats.hp],
                    ["MP", r.baseStats.mp],
                  ]
                    .filter(([, v]) => v > 0)
                    .map(([label, v]) => `${label} ${formatNumber(v)}`)
                    .join(" · ") || "-",
          },
          {
            key: "note",
            label: "",
            wrap: true,
            render: (r) => (
              <MDBox display="flex" gap={0.5} flexWrap="wrap">
                {!r.empty && !r.checksStats && (
                  <Chip size="small" variant="outlined" label="không xét chỉ số" />
                )}
                {!r.empty && !r.canFusion && (
                  <Chip size="small" color="warning" label="không dung hợp được" />
                )}
              </MDBox>
            ),
          },
          {
            key: "pick",
            label: "",
            align: "right",
            render: (r) =>
              r.empty ? null : (
                <MDButton
                  size="small"
                  variant="outlined"
                  color="info"
                  onClick={() => onPick(r.type, r.templateId)}
                >
                  Chỉ ô này
                </MDButton>
              ),
          },
        ]}
      />
    </MDBox>
  );
}

BestSet.defaultProps = { error: null };

BestSet.propTypes = {
  stat: PropTypes.string.isRequired,
  onStat: PropTypes.func.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onPickAll: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  meta: PropTypes.object.isRequired,
};

function StatCell({ item }) {
  const parts = [
    ["công", item.atkRange],
    ["thủ", item.defRange],
    ["HP", item.hpRange],
    ["MP", item.mpRange],
  ].filter(([, range]) => topOf(range) > 0);
  if (parts.length === 0) return "-";
  return (
    <MDBox>
      {parts.map(([label, range]) => (
        <MDTypography variant="caption" display="block" key={label}>
          {`${label} ${rangeText(range)}`}
        </MDTypography>
      ))}
    </MDBox>
  );
}

StatCell.propTypes = { item: PropTypes.object.isRequired };

/**
 * Danh mục toàn bộ trang bị trong cơ sở dữ liệu, kể cả món pet chưa đủ chỉ số để mang —
 * để xem trong game có những gì và còn thiếu bao nhiêu điểm thì với tới.
 *
 * Cố ý KHÔNG hiện chỉ số sau cường hoá / dung hợp: con số đó phải do máy chủ tính. Bấm "Chọn"
 * là món vào thẳng ô tương ứng của bộ giả lập, rồi đọc chỉ số thật ở bảng kết quả.
 */
const MODES = [
  { value: "list", label: "Từng món" },
  { value: "best", label: "Bộ tốt nhất" },
];

function GearBrowserDialog({
  meta,
  petStats,
  defaultType,
  defaultMode,
  onClose,
  onPick,
  onPickAll,
}) {
  const types = useMemo(
    () => [
      ...meta.equipSlots,
      { type: meta.wingType, label: "Cánh" },
      { type: meta.skinType, label: "Cải trang" },
    ],
    [meta]
  );

  const [type, setType] = useState(defaultType ?? types[0].type);
  const [input, setInput] = useState("");
  const [sort, setSort] = useState("id");
  const [onlyWearable, setOnlyWearable] = useState(false);
  const [mode, setMode] = useState(defaultMode);
  const [bestStat, setBestStat] = useState("atk");

  const { data, loading, error } = useFetch(
    () => api.get("/api/admin/templates/items", { type, size: PAGE_SIZE }),
    [type]
  );

  // Bộ tốt nhất tính ở máy chủ trên toàn bộ dữ liệu, không chỉ trang đang tải.
  const best = useFetch(
    () =>
      mode === "best"
        ? api.get("/api/admin/simulate/best-gear", { stat: bestStat, ...(petStats || {}) })
        : Promise.resolve(null),
    [mode, bestStat, JSON.stringify(petStats || {})]
  );

  // Cánh và cải trang không bị kiểm tra chỉ số trong game nên không xét "đeo được".
  const checksStats = meta.equipSlots.some((s) => s.type === type);

  const rows = useMemo(() => {
    let list = data?.items || [];
    const text = input.trim().toLowerCase();
    if (text) {
      list = list.filter(
        (x) => String(x.itemId) === text || (x.name || "").toLowerCase().includes(text)
      );
    }
    if (onlyWearable && checksStats && petStats) {
      list = list.filter((x) => missingFor(x, petStats).ok);
    }
    const score = {
      atk: (x) => topOf(x.atkRange),
      def: (x) => topOf(x.defRange),
      hp: (x) => topOf(x.hpRange),
      mp: (x) => topOf(x.mpRange),
    }[sort];
    if (score) {
      list = [...list].sort((a, b) => score(b) - score(a));
    } else if (sort === "require") {
      const need = (x) => x.requireStr + x.requireAgi + x.requireInt;
      list = [...list].sort((a, b) => need(a) - need(b));
    }
    return list;
  }, [data, input, sort, onlyWearable, checksStats, petStats]);

  const total = data?.total ?? 0;

  return (
    <Dialog open fullWidth maxWidth="lg" onClose={onClose}>
      <DialogTitle>Toàn bộ trang bị trong cơ sở dữ liệu</DialogTitle>
      <DialogContent dividers>
        <MDBox mb={2} maxWidth={260}>
          <Choice label="Chế độ" value={mode} options={MODES} onChange={setMode} />
        </MDBox>

        {mode === "best" ? (
          <BestSet
            stat={bestStat}
            onStat={setBestStat}
            rows={best.data || []}
            loading={best.loading}
            error={best.error}
            onPick={onPick}
            onPickAll={onPickAll}
            meta={meta}
          />
        ) : (
          <>
            <Grid container spacing={2} alignItems="center" mb={1}>
              <Grid item xs={12} md={3}>
                <Choice
                  label="Loại"
                  value={type}
                  options={types.map((t) => ({ value: t.type, label: t.label }))}
                  onChange={(next) => setType(next)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MDInput
                  fullWidth
                  size="small"
                  label="Tìm theo tên hoặc ID"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Choice label="Sắp xếp" value={sort} options={SORTS} onChange={setSort} />
              </Grid>
              <Grid item xs={12} md={2}>
                {checksStats && petStats && (
                  <MDButton
                    size="small"
                    fullWidth
                    variant={onlyWearable ? "gradient" : "outlined"}
                    color="success"
                    onClick={() => setOnlyWearable((v) => !v)}
                  >
                    {onlyWearable ? "Đang lọc" : "Chỉ món đeo được"}
                  </MDButton>
                )}
              </Grid>
            </Grid>

            {petStats ? (
              <MDTypography variant="caption" color="text" display="block" mb={1}>
                {`Pet đang có sức ${formatNumber(petStats.str)} / nhanh ${formatNumber(
                  petStats.agi
                )} / trí ${formatNumber(
                  petStats.int
                )}. Cột cuối cho biết còn thiếu bao nhiêu điểm.`}
              </MDTypography>
            ) : (
              <MDTypography variant="caption" color="text" display="block" mb={1}>
                Chọn một pet ở ngoài thì bảng này sẽ cho biết món nào pet đeo được.
              </MDTypography>
            )}

            {error && <Alert severity="error">{error}</Alert>}
            {total > PAGE_SIZE && (
              <Alert severity="info" sx={{ mb: 1 }}>
                {`Loại này có ${formatNumber(
                  total
                )} món, bảng đang hiện ${PAGE_SIZE} món đầu — gõ tên hoặc ID để tìm món còn lại.`}
              </Alert>
            )}

            <AdminTable
              loading={loading}
              rows={rows}
              rowKey="itemId"
              emptyText="Không có món nào khớp"
              columns={[
                {
                  key: "name",
                  label: "Vật phẩm",
                  wrap: true,
                  render: (item) => (
                    <MDBox display="flex" alignItems="center" gap={1.5}>
                      <GameIcon path={item.iconPath} size={32} fallback="shield" />
                      <MDBox minWidth={0}>
                        <MDTypography variant="button" fontWeight="medium" display="block">
                          {item.name}
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          {`#${item.itemId}`}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  ),
                },
                {
                  key: "stats",
                  label: "Chỉ số gốc",
                  wrap: true,
                  render: (item) => <StatCell item={item} />,
                },
                {
                  key: "require",
                  label: "Yêu cầu",
                  render: (item) => {
                    const need = [
                      ["Sức", item.requireStr],
                      ["Nhanh", item.requireAgi],
                      ["Trí", item.requireInt],
                    ].filter(([, v]) => v > 0);
                    return need.length === 0
                      ? "không"
                      : need.map(([label, v]) => `${label} ${formatNumber(v)}`).join(" · ");
                  },
                },
                {
                  key: "gap",
                  label: "Pet này",
                  wrap: true,
                  render: (item) => {
                    if (!checksStats)
                      return <Chip size="small" variant="outlined" label="không xét" />;
                    const missing = missingFor(item, petStats);
                    if (!missing) return "-";
                    if (missing.ok) return <Chip size="small" color="success" label="Đeo được" />;
                    return (
                      <Chip
                        size="small"
                        color="warning"
                        label={`thiếu ${missing.gap
                          .map(([label, v]) => `${formatNumber(v)} ${label}`)
                          .join(", ")}`}
                      />
                    );
                  },
                },
                {
                  key: "pick",
                  label: "",
                  align: "right",
                  render: (item) => (
                    <MDButton
                      size="small"
                      variant="gradient"
                      color="info"
                      onClick={() => onPick(item.type, item.itemId)}
                    >
                      <Icon>check</Icon>&nbsp;Chọn
                    </MDButton>
                  ),
                },
              ]}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>
          Đóng
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

GearBrowserDialog.defaultProps = { petStats: null, defaultType: null, defaultMode: "list" };

GearBrowserDialog.propTypes = {
  meta: PropTypes.object.isRequired,
  /** { str, agi, int } của pet đang dựng, null nếu chưa chọn pet. */
  petStats: PropTypes.object,
  /** Loại mở sẵn khi vừa bật danh mục. */
  defaultType: PropTypes.number,
  /** Mở sẵn ở chế độ nào: "list" (từng món) hoặc "best" (bộ tốt nhất). */
  defaultMode: PropTypes.oneOf(["list", "best"]),
  onClose: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  /** Mặc cả bộ: nhận danh sách [{ type, templateId }]. */
  onPickAll: PropTypes.func.isRequired,
};

export default GearBrowserDialog;
